import React, { useState, useEffect, useContext } from 'react';
import {
  Alert,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getAnalytics, logEvent, setUserId } from 'firebase/analytics';
import { Formik, Form, getIn } from 'formik';
import * as Yup from 'yup';

import logo from '../logo-trans.png';
import { useBodyClass, useLangParam } from '../hooks';
import MUI from '../mui';
import { ApiContext } from '../context/Api';
import LanguageSelect from '../components/LanguageSelect';

function SignUp(props) {
  useLangParam();
  const { apiService } = useContext(ApiContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();

  useBodyClass('signup');
  useBodyClass('auth-screen');

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('userInfo'));
    if (user) {
      navigate('/');
    }
  }, [navigate]);

  async function onSignUp(values) {
    setIsLoading(true);

    apiService
      .sendPostRequest('auth/local/register', {
        username: values.username,
        email: values.email,
        password: values.password
      })
      .then(response => {
        // Handle success.
        localStorage.setItem('userInfo', JSON.stringify(response.data));
        const analytics = getAnalytics();
        logEvent(analytics, 'sign_up', { method: 'strapi' });
        setUserId(analytics, response.data.user.id);

        setIsLoading(false);
        props.history.push('/');
      })
      .catch(error => {
        // Handle error.
        setError(error.response.data.message[0].messages[0].message);
        setIsLoading(false);
      });
  }
  return (
    <MUI mode="dark">
      <Container>
        <div className="top-lang">
          <LanguageSelect showLanguage />
        </div>
        <Stack
          direction="column"
          useFlexGap
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            marginBlockEnd: '1rem',
          }}
        >
          <img
            alt=""
            src={logo}
            width="80"
            className="d-inline-block align-top logo"
          />
          <Typography variant="h4">
            {t('core.progressingTogether')}
          </Typography>
          <Typography variant="h5">{t('auth.signUp')}</Typography>
        </Stack>
        <Formik
          initialValues={{ username: '', email: '', password: '' }}
          validationSchema={Yup.object({
            email: Yup.string()
              .email(t('core.invalidEmail'))
              .required(t('core.required')),
            username: Yup.string().required(t('core.required')),
            password: Yup.string().required(t('core.required'))
          })}
          onSubmit={(values, { setSubmitting }) => {
            onSignUp(values);
            setSubmitting(false);
          }}
        >
          {({ handleChange, errors, touched }) => (
            <Form className="form-base">
              <Stack direction="column" useFlexGap={true} spacing={2}>
                <TextField
                  label={t('auth.username') + '*'}
                  className="form-field"
                  type="text"
                  variant="filled"
                  name="username"
                  onChange={handleChange}
                  error={Boolean(
                    getIn(touched, 'username') &&
                    getIn(errors, 'username')
                  )}
                  helperText={
                    getIn(touched, 'username') &&
                    getIn(errors, 'username')
                      ? getIn(errors, 'username')
                      : ''
                  }
                />
                <TextField
                  label={t('core.email') + '*'}
                  className="form-field"
                  type="email"
                  name="email"
                  variant="filled"
                  onChange={handleChange}
                  error={Boolean(
                    getIn(touched, 'email') && getIn(errors, 'email')
                  )}
                  helperText={
                    getIn(touched, 'email') && getIn(errors, 'email')
                      ? getIn(errors, 'email')
                      : ''
                  }
                />
                <TextField
                  label={t('auth.password') + '*'}
                  className="form-field"
                  type="password"
                  name="password"
                  variant="filled"
                  onChange={handleChange}
                  error={Boolean(
                    getIn(touched, 'password') &&
                      getIn(errors, 'password')
                  )}
                  helperText={
                    getIn(touched, 'password') &&
                    getIn(errors, 'password')
                      ? getIn(errors, 'password')
                      : ''
                  }
                />

                {error && <Alert severity="error">{error}</Alert>}

                <Button
                  type="submit"
                  variant="contained"
                  color="dark"
                  size="large"
                  loading={isLoading}
                  sx={{ width: '100%', fontSize: '1.25rem' }}
                >
                  {t('auth.signUp')}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
        <div className="end-text text-center">
          {t('auth.alreadyHaveAccount') + ' '}
          <Link className="login-link-text" id="login_id" to="../signin">
            {t('auth.login')}
          </Link>
        </div>
      </Container>
    </MUI>
  );
}
export default SignUp;
